import React, { Component } from 'react';
import { X, Send, Reply, Paperclip, Image, Video, File, Share2, Forward, ShoppingCart, Plus, Trash2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { shoppingRequestWebSocketService } from '@/services/shoppingRequestWebSocket';
import { cleanUUID, isValidUUID } from '@/utils/uuid';
import { getUserIdFromToken } from '@/utils/jwt';
import { CHAT_CART_ORDER_STATUS_LABELS, type ChatCartOrderSummary, type ChatCartOrderStatus } from '@/types/chatCartOrder';
import bookingStyles from '@/components/BookingChat/BookingChat.module.scss';
import styles from './ShoppingRequestChat.module.scss';

/** A single editable row in the manual order composer. */
interface ManualOrderLine {
  itemName: string;
  quantityText: string;
  note: string;
}

const emptyOrderLine = (): ManualOrderLine => ({ itemName: '', quantityText: '', note: '' });

/** Statuses that allow chat (per SHOP_GROCERY_API: requested, confirmed, active). */
const CHAT_ALLOWED_STATUSES = ['requested', 'confirmed', 'active'];

interface ShoppingRequestChatProps {
  requestId: string;
  branchName: string;
  shopName: string;
  onClose: () => void;
  /** If parent already knows request status (e.g. from list API), pass it so chat can show immediately. */
  initialRequestStatus?: string | null;
}

interface ReplyToMessage {
  id: string;
  messageText: string;
  senderType: string;
  senderId: string;
  createdAt: string;
}

interface Attachment {
  id: string;
  attachmentType: string;
  fileUrl: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  thumbnailUrl?: string;
  linkUrl?: string;
  metadata?: any;
  createdAt?: string;
}

interface MessageReaction {
  userId: string;
  reactionType: 'like' | 'dislike';
  createdAt?: string;
}

interface ChatMessage {
  id: string;
  senderId: string;
  senderType: string;
  senderName: string;
  message: string;
  timestamp: string;
  read: boolean;
  readAt?: string;
  replyToMessage?: ReplyToMessage | null;
  attachments?: Attachment[];
  reactions?: MessageReaction[];
  cartOrder?: ChatCartOrderSummary | null;
}

interface State {
  messages: ChatMessage[];
  inputMessage: string;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  chatEnabled: boolean;
  requestStatus: string | null;
  isLoadingStatus: boolean;
  replyingTo: ChatMessage | null;
  selectedFiles: File[];
  isUploading: boolean;
  savedLists: Array<{ id: string; name: string }>;
  selectedSavedListId: string;
  forwardModalMessage: ChatMessage | null;
  otherRequests: Array<{ id: string; branch?: { name?: string } }>;
  shareCopied: boolean;
  orderModalOpen: boolean;
  orderLines: ManualOrderLine[];
  orderNotes: string;
  isCreatingOrder: boolean;
  /** True while the organizer (store) is typing. */
  organizerTyping: boolean;
  /** Cart-order id whose "request changes" note input is open. */
  cartChangeNoteOrderId: string | null;
  cartChangeNoteText: string;
  /** Cart-order id currently submitting an accept/decline/request-changes response. */
  cartRespondingId: string | null;
  /** When set, the order composer is editing this existing (unfrozen) cart order. */
  editingOrderId: string | null;
}

export default class ShoppingRequestChat extends Component<ShoppingRequestChatProps, State> {
  private messagesEndRef = React.createRef<HTMLDivElement>();
  private chatContainerRef = React.createRef<HTMLDivElement>();
  private fileInputRef = React.createRef<HTMLInputElement>();
  private currentUserId: string | null = null;
  /** Debounce timer for sending our own "stopped typing" signal. */
  private typingTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Whether we've told the server we're currently typing (avoids spamming POSTs). */
  private typingActive = false;
  /** Auto-hide timer for the incoming organizer typing indicator. */
  private organizerTypingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ShoppingRequestChatProps) {
    super(props);
    const token = apiService.getTokens().accessToken;
    this.currentUserId = token ? getUserIdFromToken(token) : null;
    const fromParent = CHAT_ALLOWED_STATUSES.includes(props.initialRequestStatus || '');
    this.state = {
      messages: [],
      inputMessage: '',
      isLoading: false,
      isSending: false,
      error: null,
      connectionStatus: 'connecting',
      chatEnabled: fromParent,
      requestStatus: props.initialRequestStatus ?? null,
      isLoadingStatus: true,
      replyingTo: null,
      selectedFiles: [],
      isUploading: false,
      savedLists: [],
      selectedSavedListId: '',
      forwardModalMessage: null,
      otherRequests: [],
      shareCopied: false,
      orderModalOpen: false,
      orderLines: [emptyOrderLine()],
      orderNotes: '',
      isCreatingOrder: false,
      organizerTyping: false,
      cartChangeNoteOrderId: null,
      cartChangeNoteText: '',
      cartRespondingId: null,
      editingOrderId: null
    };
  }

  componentDidMount() {
    const { requestId } = this.props;
    if (!requestId || !cleanUUID(requestId) || !isValidUUID(cleanUUID(requestId))) {
      this.setState({ isLoadingStatus: false, error: 'Invalid request ID.' });
      return;
    }
    this.checkChatStatus();
    this.loadSavedLists();
  }

  componentWillUnmount() {
    this.stopTyping();
    if (this.organizerTypingTimeout) clearTimeout(this.organizerTypingTimeout);
    this.removeSocketListeners();
    const cleaned = cleanUUID(this.props.requestId);
    if (cleaned) shoppingRequestWebSocketService.leaveRequestRoom(cleaned);
  }

  componentDidUpdate(_: ShoppingRequestChatProps, prevState: State) {
    if (this.state.messages.length !== prevState.messages.length) {
      this.scrollToBottom();
    }
  }

  checkChatStatus = async () => {
    const { requestId } = this.props;
    const cleaned = cleanUUID(requestId);
    if (!cleaned || !isValidUUID(cleaned)) {
      this.setState({ isLoadingStatus: false });
      return;
    }
    try {
      const res = await apiService.getShoppingRequestChatStatus(cleaned);
      const data = (res?.data ?? res) as { enabled?: boolean; requestStatus?: string; status?: string; message?: string; bookingStatus?: string } | undefined;
      const status = data?.requestStatus ?? data?.status ?? (data as any)?.bookingStatus ?? this.props.initialRequestStatus ?? null;
      const enabledByApi = data?.enabled === true;
      const enabledByStatus = status && CHAT_ALLOWED_STATUSES.includes(status);
      const enabled = enabledByApi || enabledByStatus;
      this.setState({
        chatEnabled: enabled,
        requestStatus: status,
        isLoadingStatus: false
      });
      if (enabled) {
        this.loadMessages();
        this.setupSocketListeners();
      } else {
        this.setState({ error: data?.message || 'Chat will be enabled once your request is accepted.' });
      }
    } catch (e: any) {
      this.setState({ isLoadingStatus: false, error: e?.message || 'Failed to check chat status.' });
    }
  };

  loadSavedLists = async () => {
    try {
      const res = await apiService.getSavedShoppingLists();
      const lists = (res?.data ?? res) ?? [];
      this.setState({ savedLists: Array.isArray(lists) ? lists : [] });
    } catch {
      this.setState({ savedLists: [] });
    }
  };

  setupSocketListeners = () => {
    const cleaned = cleanUUID(this.props.requestId);
    if (!cleaned) return;
    shoppingRequestWebSocketService.connect(cleaned);
    shoppingRequestWebSocketService.on('shopping_request_chat:joined', () => {
      this.setState({ connectionStatus: 'connected' });
    });
    shoppingRequestWebSocketService.on('shopping_request_message:new', this.handleNewMessage);
    shoppingRequestWebSocketService.on('shopping_request_message:reaction_added', this.handleReactionAdded);
    shoppingRequestWebSocketService.on('shopping_request_message:reaction_removed', this.handleReactionRemoved);
    shoppingRequestWebSocketService.on('shopping_request_cart_order:updated', this.handleCartOrderUpdated);
    shoppingRequestWebSocketService.on('shopping_request:typing', this.handleTyping);
    shoppingRequestWebSocketService.on('error', () => this.setState({ connectionStatus: 'error' }));
    shoppingRequestWebSocketService.on('disconnect', () => this.setState({ connectionStatus: 'disconnected' }));
  };

  removeSocketListeners = () => {
    shoppingRequestWebSocketService.off('shopping_request_chat:joined', () => {});
    shoppingRequestWebSocketService.off('shopping_request_message:new', this.handleNewMessage);
    shoppingRequestWebSocketService.off('shopping_request_message:reaction_added', this.handleReactionAdded);
    shoppingRequestWebSocketService.off('shopping_request_message:reaction_removed', this.handleReactionRemoved);
    shoppingRequestWebSocketService.off('shopping_request_cart_order:updated', this.handleCartOrderUpdated);
    shoppingRequestWebSocketService.off('shopping_request:typing', this.handleTyping);
  };

  /** Incoming `shopping_request:typing` from the organizer (arrives on user:${uid}). */
  handleTyping = (data: any) => {
    if (data?.requestId && cleanUUID(data.requestId) !== cleanUUID(this.props.requestId)) return;
    // Only react to the organizer's typing; never echo our own.
    if (data?.senderType && data.senderType !== 'organizer') return;
    const isTyping = !!data?.isTyping;
    this.setState({ organizerTyping: isTyping });
    if (this.organizerTypingTimeout) clearTimeout(this.organizerTypingTimeout);
    // Auto-hide in case the stop event is missed.
    if (isTyping) {
      this.organizerTypingTimeout = setTimeout(() => this.setState({ organizerTyping: false }), 4000);
    }
  };

  /** Notify the server we're typing (debounced); call on each keystroke. */
  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ inputMessage: e.target.value });
    const cleaned = cleanUUID(this.props.requestId);
    if (!cleaned) return;
    if (!this.typingActive) {
      this.typingActive = true;
      apiService.sendShoppingRequestTyping(cleaned, true).catch(() => {});
    }
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => this.stopTyping(), 3000);
  };

  /** Tell the server we've stopped typing (on blur, send, stop, or unmount). */
  stopTyping = () => {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    if (!this.typingActive) return;
    this.typingActive = false;
    const cleaned = cleanUUID(this.props.requestId);
    if (cleaned) apiService.sendShoppingRequestTyping(cleaned, false).catch(() => {});
  };

  mapApiMessage = (msg: any): ChatMessage => ({
    id: msg.id,
    senderId: msg.senderId || msg.sender?.id,
    senderType: msg.senderType || 'organizer',
    senderName: msg.senderName || msg.sender?.name || (msg.senderType === 'customer' ? 'You' : this.props.shopName),
    message: msg.messageText || msg.message || '',
    timestamp: msg.createdAt || msg.timestamp || '',
    read: msg.isRead || false,
    readAt: msg.readAt,
    replyToMessage: msg.replyToMessage ? {
      id: msg.replyToMessage.id,
      messageText: msg.replyToMessage.messageText,
      senderType: msg.replyToMessage.senderType,
      senderId: msg.replyToMessage.senderId,
      createdAt: msg.replyToMessage.createdAt
    } : null,
    attachments: msg.attachments?.map((a: any) => ({
      id: a.id,
      attachmentType: a.attachmentType,
      fileUrl: a.fileUrl,
      fileName: a.fileName,
      fileSize: a.fileSize,
      mimeType: a.mimeType,
      thumbnailUrl: a.thumbnailUrl,
      linkUrl: a.linkUrl,
      metadata: a.metadata,
      createdAt: a.createdAt
    })),
    reactions: msg.reactions || [],
    cartOrder: msg.cartOrder ? this.mapCartOrder(msg.cartOrder) : null
  });

  mapCartOrder = (co: any): ChatCartOrderSummary => ({
    id: co.id,
    status: co.status as ChatCartOrderStatus,
    deliverBy: co.deliverBy ?? null,
    organizerNotes: co.organizerNotes ?? null,
    itemCount: co.itemCount ?? (Array.isArray(co.items) ? co.items.length : undefined),
    estimatedTotal: co.estimatedTotal ?? null,
    total: co.total ?? null,
    acceptedAt: co.acceptedAt ?? null,
    items: Array.isArray(co.items)
      ? co.items.map((it: any) => ({
          id: it.id,
          cartOrderId: it.cartOrderId ?? co.id,
          stockItemId: it.stockItemId ?? null,
          quantity: it.quantity ?? 0,
          unitPrice: it.unitPrice ?? null,
          itemName: it.itemName ?? '',
          itemImage: it.itemImage,
          quantityText: it.quantityText ?? null,
          note: it.note ?? null,
          isAvailable: it.isAvailable !== false,
          organizerComment: it.organizerComment ?? null,
          sortOrder: it.sortOrder ?? 0,
          createdAt: it.createdAt ?? ''
        }))
      : undefined
  });

  handleNewMessage = (data: any) => {
    if (data.requestId && cleanUUID(data.requestId) !== cleanUUID(this.props.requestId)) return;
    const messageId = data.id;
    if (!messageId) return;
    this.setState((prev) => {
      // Already have this exact server message (e.g. echoed on both channels) – ignore.
      if (prev.messages.some((m) => m.id === messageId)) return prev;
      const newMsg = this.mapApiMessage(data);
      // The customer's own message is rendered optimistically with a `temp-` id.
      // When the Ably echo for that message arrives, reconcile it with the pending
      // optimistic copy (replace the temp id with the real one) instead of appending
      // a second bubble – this is the root cause of the "message shows twice" bug.
      if (newMsg.senderType === 'customer') {
        const idx = prev.messages.findIndex(
          (m) =>
            (m.id.startsWith('temp-') || m.id.startsWith('order-')) &&
            m.senderType === 'customer' &&
            // Reconcile by cart-order id when present, otherwise by message text.
            (newMsg.cartOrder && m.cartOrder
              ? m.cartOrder.id === newMsg.cartOrder.id
              : m.message.trim() === newMsg.message.trim())
        );
        if (idx !== -1) {
          const messages = prev.messages.slice();
          messages[idx] = {
            ...messages[idx],
            id: newMsg.id,
            timestamp: newMsg.timestamp || messages[idx].timestamp,
            read: newMsg.read,
            attachments:
              newMsg.attachments && newMsg.attachments.length ? newMsg.attachments : messages[idx].attachments,
            reactions: newMsg.reactions && newMsg.reactions.length ? newMsg.reactions : messages[idx].reactions
          };
          return { ...prev, messages };
        }
      }
      if (newMsg.senderType === 'organizer') {
        setTimeout(() => this.markRead([newMsg.id]), 100);
      }
      return { ...prev, messages: [...prev.messages, newMsg].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) };
    });
  };

  handleReactionAdded = (data: any) => {
    this.setState((prev) => ({
      messages: prev.messages.map((m) =>
        m.id === data.messageId
          ? {
              ...m,
              reactions: [...(m.reactions || []).filter((r) => r.userId !== data.userId), { userId: data.userId, reactionType: data.reactionType }]
            }
          : m
      )
    }));
  };

  handleReactionRemoved = (data: any) => {
    this.setState((prev) => ({
      messages: prev.messages.map((m) =>
        m.id === data.messageId ? { ...m, reactions: (m.reactions || []).filter((r) => r.userId !== data.userId) } : m
      )
    }));
  };

  /** Live update an order card when the organizer changes status / delivery time. */
  handleCartOrderUpdated = (data: any) => {
    const co = data?.cartOrder ?? data;
    const orderId = co?.id ?? data?.cartOrderId;
    if (!orderId) return;
    this.setState((prev) => ({
      messages: prev.messages.map((m) =>
        m.cartOrder && m.cartOrder.id === orderId
          ? {
              ...m,
              cartOrder: {
                ...m.cartOrder,
                status: (co?.status as ChatCartOrderStatus) ?? m.cartOrder.status,
                deliverBy: co?.deliverBy ?? m.cartOrder.deliverBy,
                organizerNotes: co?.organizerNotes ?? m.cartOrder.organizerNotes,
                estimatedTotal: co?.estimatedTotal ?? m.cartOrder.estimatedTotal,
                total: co?.total ?? m.cartOrder.total,
                acceptedAt: co?.acceptedAt ?? m.cartOrder.acceptedAt,
                items: Array.isArray(co?.items) ? this.mapCartOrder(co).items : m.cartOrder.items
              }
            }
          : m
      )
    }));
  };

  loadMessages = async () => {
    const cleaned = cleanUUID(this.props.requestId);
    if (!cleaned) return;
    this.setState({ isLoading: true, error: null });
    try {
      const res = await apiService.getShoppingRequestMessages(cleaned, 1, 50);
      const payload = (res?.data ?? res) as { messages?: any[] } | undefined;
      const list = payload?.messages ?? [];
      const messages = list.map((msg: any) => this.mapApiMessage(msg)).sort((a: ChatMessage, b: ChatMessage) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      this.setState({ messages, isLoading: false });
      const unreadIds = messages.filter((m: ChatMessage) => m.senderType === 'organizer' && !m.read).map((m: ChatMessage) => m.id);
      if (unreadIds.length > 0) this.markRead(unreadIds);
    } catch (e: any) {
      this.setState({ messages: [], isLoading: false, error: e?.message || 'Failed to load messages.' });
    }
  };

  markRead = async (messageIds: string[]) => {
    try {
      await apiService.markShoppingRequestMessagesRead(cleanUUID(this.props.requestId)!, messageIds);
    } catch {}
    this.setState((prev) => ({
      messages: prev.messages.map((m) => (messageIds.includes(m.id) ? { ...m, read: true } : m))
    }));
  };

  scrollToBottom = () => {
    this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  handleShare = async () => {
    try {
      const res = await apiService.getShoppingRequestShareLink(cleanUUID(this.props.requestId)!);
      const url = (res?.data as { shareUrl?: string } | undefined)?.shareUrl ?? (res as { shareUrl?: string })?.shareUrl;
      if (url) {
        await navigator.clipboard.writeText(url);
        this.setState({ shareCopied: true });
        setTimeout(() => this.setState({ shareCopied: false }), 2000);
      }
    } catch (e: any) {
      this.setState({ error: e?.message || 'Failed to copy link.' });
    }
  };

  handleForwardClick = (message: ChatMessage) => {
    apiService.getShoppingRequests().then((res) => {
      const list = (res?.data ?? res) ?? [];
      const others = (Array.isArray(list) ? list : []).filter((r: any) => r.id && cleanUUID(r.id) !== cleanUUID(this.props.requestId));
      this.setState({ forwardModalMessage: message, otherRequests: others });
    }).catch(() => this.setState({ otherRequests: [], forwardModalMessage: message }));
  };

  handleForwardTo = async (targetRequestId: string) => {
    const { forwardModalMessage } = this.state;
    if (!forwardModalMessage) return;
    try {
      await apiService.forwardShoppingRequestMessage(this.props.requestId, forwardModalMessage.id, targetRequestId);
      this.setState({ forwardModalMessage: null, otherRequests: [] });
    } catch (e: any) {
      this.setState({ error: e?.message || 'Forward failed.' });
    }
  };

  handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    this.stopTyping();
    const { inputMessage, replyingTo, selectedFiles, selectedSavedListId } = this.state;
    const cleaned = cleanUUID(this.props.requestId);
    if (!cleaned) return;
    const hasText = (inputMessage?.trim() || '').length > 0;
    const hasFiles = selectedFiles.length > 0;
    const hasList = !!selectedSavedListId;
    if (!hasText && !hasFiles && !hasList) return;
    if (this.state.isSending) return;

    const tempId = `temp-${Date.now()}`;
    const optimistic: ChatMessage = {
      id: tempId,
      senderId: 'customer',
      senderType: 'customer',
      senderName: 'You',
      message: inputMessage.trim() || '(List)',
      timestamp: new Date().toISOString(),
      read: false,
      replyToMessage: replyingTo ? { id: replyingTo.id, messageText: replyingTo.message, senderType: replyingTo.senderType, senderId: replyingTo.senderId, createdAt: replyingTo.timestamp } : null,
      attachments: selectedFiles.map((f, i) => ({
        id: `temp-${i}`,
        attachmentType: f.type.startsWith('image/') ? 'image' : f.type.startsWith('video/') ? 'video' : 'file',
        fileUrl: URL.createObjectURL(f),
        fileName: f.name,
        fileSize: f.size,
        mimeType: f.type,
        createdAt: ''
      })),
      reactions: []
    };

    this.setState((prev) => ({
      messages: [...prev.messages, optimistic],
      inputMessage: '',
      isSending: true,
      isUploading: hasFiles,
      replyingTo: null,
      selectedSavedListId: ''
    }));
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';

    try {
      const response = await apiService.sendShoppingRequestMessage(cleaned, {
        messageText: hasText ? inputMessage.trim() : undefined,
        replyToMessageId: replyingTo?.id,
        savedListId: hasList ? selectedSavedListId : undefined,
        attachments: hasFiles ? selectedFiles : undefined
      });
      const sent = response?.data ?? response;
      if (sent?.id) {
        this.setState((prev) => {
          // If the Ably echo already inserted the real message, drop the optimistic
          // copy instead of renaming it (which would leave two bubbles with the same id).
          const realExists = prev.messages.some((m) => m.id === sent.id && m.id !== tempId);
          const messages = realExists
            ? prev.messages.filter((m) => m.id !== tempId)
            : prev.messages.map((m) =>
                m.id === tempId
                  ? {
                      ...m,
                      id: sent.id,
                      timestamp: sent.createdAt || m.timestamp,
                      attachments: sent.attachments?.map((a: any) => ({ ...a, fileUrl: a.fileUrl || a.file })) ?? m.attachments
                    }
                  : m
              );
          return { messages, isSending: false, isUploading: false, selectedFiles: [] };
        });
      } else {
        this.setState({ isSending: false, isUploading: false, selectedFiles: [] });
      }
    } catch (err: any) {
      this.setState((prev) => ({
        messages: prev.messages.filter((m) => m.id !== tempId),
        isSending: false,
        isUploading: false,
        selectedFiles: [],
        error: err?.message || 'Failed to send.'
      }));
    }
  };

  // ---- Manual product list → order ----
  openOrderModal = () => this.setState({ orderModalOpen: true, editingOrderId: null, orderLines: [emptyOrderLine()], orderNotes: '', error: null });
  closeOrderModal = () => this.setState({ orderModalOpen: false, editingOrderId: null });

  /** Open the composer pre-filled to edit an existing (unfrozen) order. */
  openEditOrder = (order: ChatCartOrderSummary) => {
    const lines = (order.items || []).map((it) => ({
      itemName: it.itemName || '',
      quantityText: it.quantityText || (it.quantity ? String(it.quantity) : ''),
      note: it.note || ''
    }));
    this.setState({
      orderModalOpen: true,
      editingOrderId: order.id,
      orderLines: lines.length > 0 ? lines : [emptyOrderLine()],
      orderNotes: '',
      error: null
    });
  };

  updateOrderLine = (index: number, field: keyof ManualOrderLine, value: string) => {
    this.setState((prev) => ({
      orderLines: prev.orderLines.map((l, i) => (i === index ? { ...l, [field]: value } : l))
    }));
  };

  addOrderLine = () => this.setState((prev) => ({ orderLines: [...prev.orderLines, emptyOrderLine()] }));

  removeOrderLine = (index: number) => {
    this.setState((prev) => {
      const next = prev.orderLines.filter((_, i) => i !== index);
      return { orderLines: next.length > 0 ? next : [emptyOrderLine()] };
    });
  };

  handleCreateOrder = async () => {
    const cleaned = cleanUUID(this.props.requestId);
    if (!cleaned) return;
    if (this.state.isCreatingOrder) return;
    const items = this.state.orderLines
      .map((l) => ({
        itemName: l.itemName.trim(),
        quantityText: l.quantityText.trim() || undefined,
        note: l.note.trim() || undefined
      }))
      .filter((l) => l.itemName.length > 0);
    if (items.length === 0) {
      this.setState({ error: 'Add at least one product to create an order.' });
      return;
    }
    const notes = this.state.orderNotes.trim() || undefined;
    const editingId = this.state.editingOrderId;
    this.setState({ isCreatingOrder: true, error: null });

    // Editing an existing (unfrozen) order: replace its items and update the card in place.
    if (editingId) {
      try {
        const res = await apiService.updateCartOrder(editingId, { items, notes });
        const co = ((res?.data ?? res) as { cartOrder?: any } | undefined)?.cartOrder;
        this.setState((prev) => ({
          messages: prev.messages.map((m) =>
            m.cartOrder && m.cartOrder.id === editingId && co ? { ...m, cartOrder: this.mapCartOrder(co) } : m
          ),
          orderModalOpen: false,
          isCreatingOrder: false,
          editingOrderId: null,
          orderLines: [emptyOrderLine()],
          orderNotes: ''
        }));
      } catch (e: any) {
        this.setState({ isCreatingOrder: false, error: e?.message || 'Failed to update order.' });
      }
      return;
    }

    try {
      const res = await apiService.createCartOrder(cleaned, { items, notes });
      const payload = (res?.data ?? res) as { cartOrder?: any } | undefined;
      const co = payload?.cartOrder;
      if (co?.id) {
        const summary = this.mapCartOrder(co);
        const msgId = co.messageId || `order-${co.id}`;
        const optimistic: ChatMessage = {
          id: msgId,
          senderId: 'customer',
          senderType: 'customer',
          senderName: 'You',
          message: notes || `🛒 Order with ${summary.itemCount ?? items.length} item(s)`,
          timestamp: co.createdAt || new Date().toISOString(),
          read: false,
          reactions: [],
          cartOrder: summary
        };
        this.setState((prev) => ({
          messages: prev.messages.some((m) => m.id === msgId)
            ? prev.messages
            : [...prev.messages, optimistic].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
          orderModalOpen: false,
          isCreatingOrder: false,
          orderLines: [emptyOrderLine()],
          orderNotes: ''
        }));
      } else {
        this.setState({ orderModalOpen: false, isCreatingOrder: false });
      }
    } catch (e: any) {
      this.setState({ isCreatingOrder: false, error: e?.message || 'Failed to create order.' });
    }
  };

  handleReaction = async (messageId: string, reactionType: 'like' | 'dislike') => {
    const msg = this.state.messages.find((m) => m.id === messageId);
    if (!msg || !this.currentUserId) return;
    const myReaction = (msg.reactions || []).find((r) => r.userId === this.currentUserId);
    try {
      if (myReaction?.reactionType === reactionType) {
        await apiService.removeShoppingRequestMessageReaction(this.props.requestId, messageId);
      } else {
        await apiService.addShoppingRequestMessageReaction(this.props.requestId, messageId, reactionType);
      }
    } catch {}
  };

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const valid = files.filter((f) => f.size <= 50 * 1024 * 1024);
    this.setState((prev) => ({ selectedFiles: [...prev.selectedFiles, ...valid].slice(0, 10) }));
  };

  handleRemoveFile = (index: number) => {
    this.setState((prev) => ({ selectedFiles: prev.selectedFiles.filter((_, i) => i !== index) }));
  };

  formatTime = (t: string) => new Date(t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  formatFileSize = (bytes: number) => (bytes < 1024 ? bytes + ' B' : bytes < 1024 * 1024 ? (bytes / 1024).toFixed(2) + ' KB' : (bytes / 1024 / 1024).toFixed(2) + ' MB');

  renderAttachment = (att: Attachment): React.ReactNode => {
    if (att.attachmentType === 'image') {
      return (
        <div key={att.id} className={bookingStyles.attachment}>
          <div className={bookingStyles.attachmentImage}>
            <img src={att.fileUrl} alt={att.fileName || 'Image'} onClick={() => window.open(att.fileUrl, '_blank')} loading="lazy" />
            {att.fileName && <div className={bookingStyles.attachmentFileName}>{att.fileName}</div>}
          </div>
        </div>
      );
    }
    if (att.attachmentType === 'video') {
      return (
        <div key={att.id} className={bookingStyles.attachment}>
          <div className={bookingStyles.attachmentVideo}>
            <video controls preload="metadata" playsInline src={att.fileUrl} style={{ maxWidth: '100%', maxHeight: 400 }} />
            {att.fileName && <div className={bookingStyles.attachmentFileName}>{att.fileName}</div>}
          </div>
        </div>
      );
    }
    return (
      <div key={att.id} className={bookingStyles.attachment}>
        <a href={att.fileUrl} target="_blank" rel="noopener noreferrer" className={bookingStyles.attachmentFile}>
          <File size={20} />
          <div className={bookingStyles.attachmentFileInfo}>
            <div className={bookingStyles.attachmentFileName}>{att.fileName || 'Download'}</div>
            {att.fileSize && <div className={bookingStyles.attachmentFileSize}>{this.formatFileSize(att.fileSize)}</div>}
          </div>
        </a>
      </div>
    );
  };

  /** Customer responds to a priced cart order (accept / decline / request changes). */
  handleRespondOrder = async (orderId: string, action: 'accept' | 'decline' | 'request_changes', note?: string) => {
    if (this.state.cartRespondingId) return;
    this.setState({ cartRespondingId: orderId });
    const optimisticStatus: ChatCartOrderStatus = action === 'accept' ? 'accepted' : 'changes_requested';
    try {
      const res = await apiService.respondCartOrder(orderId, { action, note });
      const co = (res?.data as any)?.cartOrder ?? (res as any)?.cartOrder;
      this.setState((prev) => ({
        cartRespondingId: null,
        cartChangeNoteOrderId: null,
        cartChangeNoteText: '',
        messages: prev.messages.map((m) =>
          m.cartOrder && m.cartOrder.id === orderId
            ? {
                ...m,
                cartOrder: co
                  ? this.mapCartOrder(co)
                  : {
                      ...m.cartOrder,
                      status: optimisticStatus,
                      acceptedAt: action === 'accept' ? new Date().toISOString() : m.cartOrder.acceptedAt
                    }
              }
            : m
        )
      }));
    } catch (e: any) {
      this.setState({ cartRespondingId: null, error: e?.message || 'Failed to send response.' });
    }
  };

  renderCartOrder = (order: ChatCartOrderSummary): React.ReactNode => {
    const statusLabel = CHAT_CART_ORDER_STATUS_LABELS[order.status] || order.status;
    const items = order.items || [];
    const deliverBy = order.deliverBy
      ? new Date(order.deliverBy).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
      : null;
    const grandTotal = order.total != null ? order.total : order.estimatedTotal;
    const isPriced = order.status === 'priced';
    const canEdit = !order.acceptedAt && ['shared', 'priced', 'changes_requested'].includes(order.status);
    const noteOpen = this.state.cartChangeNoteOrderId === order.id;
    const responding = this.state.cartRespondingId === order.id;
    return (
      <div className={styles.cartOrderCard}>
        <div className={styles.cartOrderHeader}>
          <span className={styles.cartOrderTitle}><ShoppingCart size={14} /> Order</span>
          <span className={styles.cartOrderStatus} data-status={order.status}>{statusLabel}</span>
        </div>
        {items.length > 0 && (
          <ul className={styles.cartOrderItems}>
            {items.map((it) => {
              const unavailable = it.isAvailable === false;
              const lineTotal = it.unitPrice != null ? it.unitPrice * (it.quantity || 1) : null;
              return (
                <li key={it.id} className={`${styles.cartOrderItem} ${unavailable ? styles.cartOrderItemUnavailable : ''}`}>
                  <div className={styles.cartOrderItemMain}>
                    <span className={styles.cartOrderItemName}>
                      {it.itemName}
                      {unavailable && <span className={styles.cartOrderUnavailableTag}>Unavailable</span>}
                    </span>
                    <span className={styles.cartOrderItemQty}>
                      {it.quantityText ? it.quantityText : it.quantity ? `x ${it.quantity}` : ''}
                      {it.unitPrice != null
                        ? ` · ₹${it.unitPrice}${lineTotal != null && (it.quantity || 1) > 1 ? ` = ₹${lineTotal}` : ''}`
                        : ''}
                    </span>
                  </div>
                  {it.note ? <span className={styles.cartOrderItemNote}>{it.note}</span> : null}
                  {it.organizerComment ? <span className={styles.cartOrderItemComment}>🛈 {it.organizerComment}</span> : null}
                </li>
              );
            })}
          </ul>
        )}
        {typeof order.itemCount === 'number' && items.length === 0 && (
          <p className={styles.cartOrderMeta}>{order.itemCount} item(s)</p>
        )}
        {grandTotal != null && Number(grandTotal) > 0 && (
          <p className={styles.cartOrderTotal}>Total: ₹{grandTotal}</p>
        )}
        {deliverBy && <p className={styles.cartOrderMeta}>Deliver by: {deliverBy}</p>}
        {order.organizerNotes && <p className={styles.cartOrderMeta}>Note: {order.organizerNotes}</p>}

        {canEdit && !noteOpen && (
          <div className={styles.cartOrderActions}>
            <button type="button" className={styles.cartOrderChangeBtn} onClick={() => this.openEditOrder(order)}>
              ✎ Edit order
            </button>
          </div>
        )}

        {isPriced && (
          <div className={styles.cartOrderActions}>
            {!noteOpen ? (
              <>
                <button type="button" className={styles.cartOrderAcceptBtn} disabled={responding} onClick={() => this.handleRespondOrder(order.id, 'accept')}>
                  {responding ? '…' : 'Accept'}
                </button>
                <button type="button" className={styles.cartOrderDeclineBtn} disabled={responding} onClick={() => this.handleRespondOrder(order.id, 'decline')}>
                  Decline
                </button>
                <button type="button" className={styles.cartOrderChangeBtn} disabled={responding} onClick={() => this.setState({ cartChangeNoteOrderId: order.id, cartChangeNoteText: '' })}>
                  Request changes
                </button>
              </>
            ) : (
              <div className={styles.cartOrderNoteRow}>
                <input
                  type="text"
                  className={styles.cartOrderNoteInput}
                  placeholder="What should change?"
                  value={this.state.cartChangeNoteText}
                  onChange={(e) => this.setState({ cartChangeNoteText: e.target.value })}
                />
                <button type="button" className={styles.cartOrderAcceptBtn} disabled={responding} onClick={() => this.handleRespondOrder(order.id, 'request_changes', this.state.cartChangeNoteText.trim() || undefined)}>
                  Send
                </button>
                <button type="button" className={styles.cartOrderDeclineBtn} disabled={responding} onClick={() => this.setState({ cartChangeNoteOrderId: null, cartChangeNoteText: '' })}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
        {order.status === 'accepted' && <p className={styles.cartOrderAcceptedChip}>✓ You accepted this order</p>}
        {order.status === 'changes_requested' && (
          <p className={styles.cartOrderMeta}>Changes requested — awaiting an updated quote.</p>
        )}
      </div>
    );
  };

  render() {
    const { requestId, branchName, shopName, onClose } = this.props;
    const { messages, inputMessage, isLoading, isSending, isUploading, error, connectionStatus, chatEnabled, isLoadingStatus, replyingTo, selectedFiles, savedLists, selectedSavedListId, forwardModalMessage, otherRequests, shareCopied, orderModalOpen, orderLines, orderNotes, isCreatingOrder } = this.state;

    if (!requestId) {
      return (
        <div className={bookingStyles.bookingChatContainer}>
          <div className={bookingStyles.chatHeader}>
            <h3>Error</h3>
            <button className={bookingStyles.closeButton} onClick={onClose}><X size={20} /></button>
          </div>
          <div className={bookingStyles.chatMessages}><p>Request ID is missing.</p></div>
        </div>
      );
    }

    if (isLoadingStatus) {
      return (
        <div className={bookingStyles.bookingChatContainer}>
          <div className={bookingStyles.chatHeader}>
            <div className={bookingStyles.chatHeaderInfo}><h3>Chat – {shopName}</h3></div>
            <button className={bookingStyles.closeButton} onClick={onClose}><X size={20} /></button>
          </div>
          <div className={bookingStyles.chatMessages}>
            <div className={bookingStyles.emptyChat}><div className={bookingStyles.loadingSpinner}></div><p>Checking chat...</p></div>
          </div>
        </div>
      );
    }

    if (!chatEnabled) {
      return (
        <div className={bookingStyles.bookingChatContainer}>
          <div className={bookingStyles.chatHeader}>
            <div className={bookingStyles.chatHeaderInfo}><h3>Chat – {shopName}</h3></div>
            <button className={bookingStyles.closeButton} onClick={onClose}><X size={20} /></button>
          </div>
          <div className={bookingStyles.chatMessages}>
            <div className={bookingStyles.emptyChat}><p>Chat not available.</p><p className={bookingStyles.emptyChatHint}>{error || 'Chat will be enabled once your request is accepted.'}</p></div>
          </div>
        </div>
      );
    }

    return (
      <div className={bookingStyles.bookingChatContainer}>
        <div className={bookingStyles.chatHeader}>
          <div className={bookingStyles.chatHeaderInfo}>
            <div className={bookingStyles.chatAvatar}>🛒</div>
            <div className={bookingStyles.chatUserInfo}>
              <h3>{shopName}</h3>
              <div className={bookingStyles.chatHeaderMeta}>
                <span className={bookingStyles.chatSubtitle}>{branchName}</span>
                <span className={`${bookingStyles.connectionStatus} ${bookingStyles[connectionStatus]}`}>
                  {connectionStatus === 'connected' && '🟢'}
                  {connectionStatus === 'connecting' && '🟡'}
                  {(connectionStatus === 'disconnected' || connectionStatus === 'error') && '🔴'}
                </span>
              </div>
            </div>
          </div>
          <div className={bookingStyles.chatHeaderActions}>
            <button type="button" className={bookingStyles.viewBookingsButton} onClick={this.handleShare} title="Copy share link">
              {shareCopied ? '✓ Copied' : <Share2 size={18} />}
            </button>
            <button className={bookingStyles.closeButton} onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {error && <div className={bookingStyles.errorBanner}>{error}</div>}

        <div className={bookingStyles.chatMessages} ref={this.chatContainerRef}>
          {isLoading ? (
            <div className={bookingStyles.emptyChat}><div className={bookingStyles.loadingSpinner}></div><p>Loading messages...</p></div>
          ) : messages.length === 0 ? (
            <div className={bookingStyles.emptyChat}><p>Start a conversation with {shopName}</p></div>
          ) : (
            <>
              {messages.map((message) => {
                const myReaction = (message.reactions || []).find((r) => r.userId === this.currentUserId);
                const likeCount = (message.reactions || []).filter((r) => r.reactionType === 'like').length;
                const dislikeCount = (message.reactions || []).filter((r) => r.reactionType === 'dislike').length;
                return (
                  <div
                    key={message.id}
                    className={`${bookingStyles.messageBubble} ${message.senderType === 'customer' ? bookingStyles.messageCustomer : bookingStyles.messageOrganizer} ${message.id.startsWith('temp-') ? bookingStyles.messagePending : ''}`}
                  >
                    {message.senderType !== 'customer' && <div className={bookingStyles.messageSender}>{message.senderName}</div>}
                    {message.replyToMessage && (
                      <div className={bookingStyles.replyPreview}>
                        <div className={bookingStyles.replyPreviewSender}>{message.replyToMessage.senderType === 'customer' ? 'You' : shopName}</div>
                        <div className={bookingStyles.replyPreviewText}>{message.replyToMessage.messageText?.slice(0, 50)}{(message.replyToMessage.messageText?.length || 0) > 50 ? '...' : ''}</div>
                      </div>
                    )}
                    <div
                      className={bookingStyles.messageText}
                      dangerouslySetInnerHTML={{
                        __html: (message.message != null ? message.message : '').replace(/\n/g, '<br />')
                      }}
                    />
                    {message.attachments && message.attachments.length > 0 && (
                      <div className={bookingStyles.messageAttachments}>{message.attachments.map((a) => this.renderAttachment(a))}</div>
                    )}
                    {message.cartOrder && this.renderCartOrder(message.cartOrder)}
                    <div className={bookingStyles.messageFooter}>
                      <div className={bookingStyles.messageTime}>{this.formatTime(message.timestamp)}</div>
                      {message.senderType === 'customer' && <div className={bookingStyles.messageStatus}>{message.id.startsWith('temp-') ? '⏳' : message.read ? '✓✓' : '✓'}</div>}
                      {message.senderType === 'organizer' && (
                        <button className={bookingStyles.replyButton} onClick={() => this.setState({ replyingTo: message })} title="Reply"><Reply size={14} /></button>
                      )}
                    </div>
                    <div className={styles.messageActionsRow}>
                      <div className={styles.reactionRow}>
                        <button type="button" className={`${styles.reactionButton} ${myReaction?.reactionType === 'like' ? styles.active : ''}`} onClick={() => this.handleReaction(message.id, 'like')} title="Like">
                          👍 {likeCount > 0 && likeCount}
                        </button>
                        <button type="button" className={`${styles.reactionButton} ${myReaction?.reactionType === 'dislike' ? styles.active : ''}`} onClick={() => this.handleReaction(message.id, 'dislike')} title="Dislike">
                          👎 {dislikeCount > 0 && dislikeCount}
                        </button>
                      </div>
                      <button type="button" className={styles.reactionButton} onClick={() => this.handleForwardClick(message)} title="Forward"><Forward size={14} /> Forward</button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {this.state.organizerTyping && (
            <div className={bookingStyles.typingIndicator}>
              <div className={bookingStyles.typingDots}><span></span><span></span><span></span></div>
              <span className={bookingStyles.typingText}>{shopName} is typing...</span>
            </div>
          )}
          <div ref={this.messagesEndRef} />
        </div>

        {replyingTo && (
          <div className={bookingStyles.replyIndicator}>
            <div className={bookingStyles.replyIndicatorContent}>
              <div className={bookingStyles.replyIndicatorInfo}>
                <span className={bookingStyles.replyLabel}>Replying to:</span>
                <span className={bookingStyles.replyText}>{replyingTo.message.slice(0, 50)}{replyingTo.message.length > 50 ? '...' : ''}</span>
              </div>
              <button className={bookingStyles.cancelReplyButton} onClick={() => this.setState({ replyingTo: null })}><X size={16} /></button>
            </div>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className={bookingStyles.selectedFilesPreview}>
            <div className={bookingStyles.selectedFilesHeader}>
              <span className={bookingStyles.selectedFilesCount}>{selectedFiles.length} file(s)</span>
              <button className={bookingStyles.clearAllFilesButton} onClick={() => this.setState({ selectedFiles: [] })}>Clear</button>
            </div>
            <div className={bookingStyles.selectedFilesList}>
              {selectedFiles.map((file, i) => (
                <div key={i} className={bookingStyles.selectedFileItem}>
                  <div className={bookingStyles.selectedFileIcon}>{file.type.startsWith('image/') ? <Image size={16} /> : file.type.startsWith('video/') ? <Video size={16} /> : <File size={16} />}</div>
                  <div className={bookingStyles.selectedFileInfo}>
                    <div className={bookingStyles.selectedFileName}>{file.name}</div>
                    <div className={bookingStyles.selectedFileSize}>{this.formatFileSize(file.size)}</div>
                  </div>
                  <button className={bookingStyles.removeFileButton} onClick={() => this.handleRemoveFile(i)}><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={this.handleSendMessage} className={bookingStyles.chatInput}>
          <input ref={this.fileInputRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.txt" onChange={this.handleFileSelect} style={{ display: 'none' }} />
          <button type="button" className={bookingStyles.attachButton} onClick={() => this.fileInputRef.current?.click()} disabled={isSending || selectedFiles.length >= 10} title="Attach"><Paperclip size={20} /></button>
          <button type="button" className={bookingStyles.attachButton} onClick={this.openOrderModal} disabled={isSending} title="Create order from a product list"><ShoppingCart size={20} /></button>
          {savedLists.length > 0 && (
            <select className={styles.savedListSelect} value={selectedSavedListId} onChange={(e) => this.setState({ selectedSavedListId: e.target.value })}>
              <option value="">List (optional)</option>
              {savedLists.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}
          <input type="text" value={inputMessage} onChange={this.handleInputChange} onBlur={this.stopTyping} placeholder={replyingTo ? 'Reply...' : 'Type a message...'} className={bookingStyles.inputField} disabled={isSending} />
          <button type="submit" className={bookingStyles.sendButton} disabled={(!inputMessage.trim() && selectedFiles.length === 0 && !selectedSavedListId) || isSending || isUploading}>
            {isSending || isUploading ? <div className={bookingStyles.sendSpinner}></div> : <Send size={20} />}
          </button>
        </form>

        {forwardModalMessage && (
          <div className={styles.forwardModalOverlay} onClick={() => this.setState({ forwardModalMessage: null, otherRequests: [] })}>
            <div className={styles.forwardModal} onClick={(e) => e.stopPropagation()}>
              <h3 className={styles.forwardModalTitle}>Forward to</h3>
              <ul className={styles.forwardList}>
                {otherRequests.length === 0 ? <li>No other requests to forward to.</li> : otherRequests.map((r) => (
                  <li key={r.id} className={styles.forwardListItem} onClick={() => this.handleForwardTo(r.id)}>
                    {r.branch?.name || r.id}
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => this.setState({ forwardModalMessage: null, otherRequests: [] })}>Cancel</button>
            </div>
          </div>
        )}

        {orderModalOpen && (
          <div className={styles.forwardModalOverlay} onClick={this.closeOrderModal}>
            <div className={styles.orderModal} onClick={(e) => e.stopPropagation()}>
              <h3 className={styles.forwardModalTitle}>{this.state.editingOrderId ? 'Edit order' : 'Create order'}</h3>
              <p className={styles.orderModalHint}>
                {this.state.editingOrderId
                  ? 'Update your items. Saving sends the order back to the shop to re-price.'
                  : 'Type the products you need — no need to browse the catalog. The organizer prices and confirms them.'}
              </p>
              <div className={styles.orderLinesList}>
                {orderLines.map((line, i) => (
                  <div key={i} className={styles.orderLineRow}>
                    <input
                      type="text"
                      className={styles.orderLineName}
                      placeholder="Product (e.g. Tomatoes)"
                      value={line.itemName}
                      onChange={(e) => this.updateOrderLine(i, 'itemName', e.target.value)}
                    />
                    <input
                      type="text"
                      className={styles.orderLineQty}
                      placeholder="Qty (e.g. 2 kg)"
                      value={line.quantityText}
                      onChange={(e) => this.updateOrderLine(i, 'quantityText', e.target.value)}
                    />
                    <input
                      type="text"
                      className={styles.orderLineNote}
                      placeholder="Note (optional)"
                      value={line.note}
                      onChange={(e) => this.updateOrderLine(i, 'note', e.target.value)}
                    />
                    <button type="button" className={styles.orderLineRemove} onClick={() => this.removeOrderLine(i)} title="Remove"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.orderAddLine} onClick={this.addOrderLine}><Plus size={16} /> Add product</button>
              <textarea
                className={styles.orderNotes}
                placeholder="Order notes (optional)"
                value={orderNotes}
                onChange={(e) => this.setState({ orderNotes: e.target.value })}
                rows={2}
              />
              <div className={styles.orderModalActions}>
                <button type="button" className={styles.orderCancelButton} onClick={this.closeOrderModal} disabled={isCreatingOrder}>Cancel</button>
                <button type="button" className={styles.orderSubmitButton} onClick={this.handleCreateOrder} disabled={isCreatingOrder}>
                  {isCreatingOrder ? 'Saving…' : this.state.editingOrderId ? 'Save changes' : 'Send order'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
